/**
 * Background:
 * Developer 'owns' the suggestion
 * Builder 'owns' the pricing (and cannot see anyone else's pricing)
 *
 * interface VEListItem {
 *     suggestion: VESuggestion;
 *     prices: VEPricing[];
 * }
 *
 * (The Builder Version of this will be simple <suggestion,price> table)
 *
 *  */
import {
    WithLoggedInAuthInfoProps,
    withRequiredAuthInfo,
} from "@propelauth/react";
import {useParams} from "react-router-dom";
import React, {useEffect} from "react";

import {VEListItem} from "./ve_model";
import {CardContent, CardHeader, CardTitle} from "../ui/card";

import {Button} from "../ui/button";
import {ArrowUpDown,  PencilLine} from "lucide-react";
import {DotsHorizontalIcon, Pencil1Icon} from "@radix-ui/react-icons";
import VeItemForm from "./veItemView";
import {fetchWithToken} from "../../api/apiBase";
import {getOrdinal} from "../builder/AuctionStatusView";
import {Badge} from "../ui/badge"
import  {CopyButton2} from "../exportData/CopyButton";
import { moneyFormatter } from "../../lib/formatters";
import ScrollTable, { Column } from "../ui/ScrollableTable";
import {SortableHeader} from "../ui/tableUtils";

const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});


/**
 * BUILDER ONLY
 *
 * There are 2 cases here:
 * 1 - they are working on their own suggestion.
 *
 * 2 - they are working on a suggestion shared with them.
 *      - they will then need to see the original
 * */
function BuilderVeView(props: WithLoggedInAuthInfoProps) {
    const {project_id} = useParams<string>();
    const [veItems, setVeItems] = React.useState<VEListItem[]>([]);

    const [isEditing, setIsEditing] = React.useState<boolean>(false);

    const [editingItem, setEditingItem] = React.useState<VEListItem | null>(null);
    const [suggestionId, setSuggestionId] = React.useState<string | null>(null);
    const [priceId, setPriceId] = React.useState<string | null>(null);

    useEffect(() => {
        setVeItems([]);
        if (project_id) {
            fetchWithToken(`/projects/${project_id}/builder/ve`, props.accessToken)
                .then((data) => {
                    console.log(data)
                    setVeItems(data);
                })
                .catch((err) => {
                    console.warn("Error:", err);

                });
        }
    }, [project_id]);

    const onSuggestVE = (e: any) => {
        e.preventDefault();
        onCloseVE();
        setIsEditing(true);
    };

    const onCloseVE = () => {
        setIsEditing(false);
        setSuggestionId(null);
        setEditingItem(null);
        setPriceId(null);
    };

    const onOpenItem = (listItem: VEListItem) => {
        console.log('item', listItem)
        setSuggestionId(listItem.suggestion.id);
        if (listItem.price){
            setPriceId(listItem.price.id)
        }
        setEditingItem(listItem);
        setIsEditing(true);
    };

    const updateListItem = (row: VEListItem) => {
        const newItems = veItems.map((item) => {

            if (item.suggestion.id === row.suggestion.id) {
                return { suggestion: item.suggestion, price: row.price};
            } else {
                return item;
            }
        });
        setVeItems(newItems);
    };

    const insertListItem = (row: VEListItem) => {

        setVeItems((xs) => [row, ...xs ]);
        setSuggestionId(null);
        setEditingItem(null);
        setPriceId(null);
    };

    const hideListItem = (row: VEListItem) => {
        const newItems = veItems.filter((item) => {
            if (item.suggestion.id !== row.suggestion.id) {
                return item;
            }
        });
        setVeItems(newItems);
        onCloseVE();
    };

    const _columns: any[] = [
        {
            header: " ",
            cell: (row: any) => {
                return (
                    <div>
                        <Button
                            variant="outline"
                            onClick={() => onOpenItem(row.original) }>
                            <Pencil1Icon className="h-3 w-3"/>
                        </Button>
                    </div>
                );
            },
        },
        {
            header: "Suggestion",
            accessorKey: "price.title",
            cell: (row:any) => {
                return <div>{row.original.price  && row.original.price.title
                    ? row.original.price.title
                    : row.original.suggestion.title}</div>;
            },
        },
        {
            header: "Description",
            id: "price.description",
            cell: (row:any) => {
                // return <div>{row.original.price && row.original.price.description
                //     ? row.original.price.description
                //     : row.original.suggestion.description}</div>;
                return <div>{row.original.suggestion.description}</div>
            },
        },
        {
            header: "Rank",
            id: "feedback",
            cell: (row:any) => {
                let fb = row.original.price?.feedback;
                if(fb){
                    let ord = getOrdinal(fb.rank + 1);
                    return <Badge variant="outline">{fb.rank + 1}{ord}</Badge>
                }
                return <></>
            }
        },
        {
            header: ({column}:any) => (<SortableHeader column={column} name={"Created"}/>),
            accessorKey: "suggestion.created_at",
            cell: (row:any) => {
                let date = new Date(row.original.suggestion?.created_at);
                return <span>{date.toLocaleDateString()}</span>;
            }
        },
        {
            header: ({column}:any) => (<SortableHeader column={column} name={"Suggested By"}/>),
            accessorKey: "suggestion.created_by.company.name",
            cell: (row:any) => {
                if (!row.original.suggestion.created_by) {
                    return <div className="text-sm">Others</div>
                }
                return (
                    <div>
                        <div className="text-xs"> {row.original.suggestion.created_by.company.name}</div>
                        <div className="text-xs">{row.original.suggestion.created_by.name}</div>
                    </div>
                );
            },
        },
        {
            header: "Savings",
            cell: (row:any) => {
                if (row.original.price) {
                    return <div>{formatted.format(row.original.price?.savings_amount)}</div>;
                } else {
                    return <div>Awaiting Price</div>
                }
            },
        },
    ];

    const formatDataToExport = () => {
        if (!veItems) {
            return [];
        }
        return veItems.map(item => ({
                suggestion: item?.price ? item?.price?.title : item.suggestion.title,
                description: item.price?.description,
                rank: item?.price?.feedback?.rank
                    ? `${item.price.feedback.rank + 1}${getOrdinal(item.price.feedback.rank + 1)}`
                    : null,
                suggestedBy: item.suggestion.created_by?.company.name || 'Others',
                savings: item.price ? moneyFormatter.format(item.price.savings_amount) : 'Awaiting Price'

            }))
    }

    return (
        <div className="container max-w-full overflow-x-hidden">
            <div className={`flex ${isEditing ? "flex-row" : "flex-col"}`}>
                <div className={` ${isEditing ? "w-2/3" : "w-full"} `}>
                    <br/>
                    <CardHeader>
                        <CardTitle>
                            <div className="flex justify-between">
                                <div>Value Engineering</div>
                                <div className="flex gap-4">
                                    <CopyButton2 dataTransform={formatDataToExport} />
                                    <Button onClick={onSuggestVE}>
                                        Suggest VE
                                    </Button>
                                </div>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="w-full">
                            <ScrollTable data={veItems} columns={_columns}/>
                        </div>
                    </CardContent>
                </div>
                {isEditing && (
                    <div className="w-1/3 ml-4">
                        <div className="flex justify-end mb-2">
                            <Button onClick={onCloseVE}>Close</Button>
                        </div>
                        {/*<VeItemBuilderView suggestion_id={editingItem}/>*/}
                        <VeItemForm
                            // do i need these? .... not really
                            suggestion_id={suggestionId}
                            price_id={priceId}
                            setItemId={setPriceId}
                            setSuggestionId={setSuggestionId}

                            // i need these
                            updateItem={updateListItem}
                            insertItem={insertListItem}
                            hideItem={hideListItem}

                            suggestionPrice={editingItem}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default withRequiredAuthInfo(BuilderVeView);
